import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Keeps N <audio> elements locked to one master clock with solo/mute + A/B routing.
export const useSyncedPlayer = (tracks) => {
  const audiosRef = useRef({});
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [solo, setSolo] = useState([]);
  const [muted, setMuted] = useState(["original"]);
  const [ab, setAb] = useState("free"); // free | A (original) | B (reconstructed)
  const [ready, setReady] = useState(false);
  const keys = useMemo(() => tracks.map((t) => t.key).join("|"), [tracks]);

  useEffect(() => {
    const audios = {};
    let loaded = 0;
    tracks.forEach((t) => {
      const a = new Audio();
      a.preload = "auto";
      a.crossOrigin = "anonymous";
      a.src = t.url;
      a.addEventListener("loadedmetadata", () => {
        loaded += 1;
        setDuration((d) => Math.max(d, a.duration || 0));
        if (loaded === tracks.length) setReady(true);
      });
      a.addEventListener("ended", () => setPlaying(false));
      audios[t.key] = a;
    });
    audiosRef.current = audios;
    setReady(false);
    setTime(0);
    return () => {
      Object.values(audios).forEach((a) => { a.pause(); a.src = ""; });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys]);

  const audible = useCallback((key) => {
    if (ab === "A") return key === "original";
    if (ab === "B") {
      const hasDME = ["dialogue", "music", "effects"].every((k) => audiosRef.current[k]);
      if (hasDME) return ["dialogue", "music", "effects"].includes(key);
      return key === "dialogue" || key === "me";
    }
    if (solo.length) return solo.includes(key);
    return !muted.includes(key);
  }, [ab, solo, muted]);

  useEffect(() => {
    Object.entries(audiosRef.current).forEach(([k, a]) => { a.muted = !audible(k); });
  }, [audible, ready]);

  useEffect(() => {
    if (!playing) return;
    let raf;
    const master = audiosRef.current.original || Object.values(audiosRef.current)[0];
    let lastSync = 0;
    const tick = () => {
      if (!master) return;
      setTime(master.currentTime);
      const now = performance.now();
      if (now - lastSync > 400) {
        lastSync = now;
        Object.values(audiosRef.current).forEach((a) => {
          if (a !== master && Math.abs(a.currentTime - master.currentTime) > 0.07) a.currentTime = master.currentTime;
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const play = useCallback(async () => {
    const list = Object.values(audiosRef.current);
    const master = audiosRef.current.original || list[0];
    list.forEach((a) => { a.currentTime = master.currentTime; });
    await Promise.all(list.map((a) => a.play().catch(() => {})));
    setPlaying(true);
  }, []);

  const pause = useCallback(() => {
    Object.values(audiosRef.current).forEach((a) => a.pause());
    setPlaying(false);
  }, []);

  const seek = useCallback((t) => {
    const clamped = Math.max(0, Math.min(t, duration || t));
    Object.values(audiosRef.current).forEach((a) => { a.currentTime = clamped; });
    setTime(clamped);
  }, [duration]);

  const stop = useCallback(() => { pause(); seek(0); }, [pause, seek]);
  const toggleSolo = (k) => setSolo((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const toggleMute = (k) => setMuted((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k]));
  const soloOnly = useCallback((k) => { setAb("free"); setSolo(k ? [k] : []); }, []);

  return { playing, time, duration, ready, solo, muted, ab, setAb, play, pause, stop, seek, toggleSolo, toggleMute, soloOnly, audible };
};
